import { Injectable } from '@angular/core';
import { User } from 'src/app/interfaces/user';
import { RegistrationFlowStep } from 'src/app/constants/registration-flow.constants';
import { FeatureFlagsService } from '../feature-flag/feature-flag.service';
import { RegistrationFlowService } from '../registration-flow/registration-flow.service';

export interface RegistrationProgressData {
  readonly isFullNameRegistrationCompleted: boolean;
  readonly isPetChipNumberRegistrationCompleted: boolean;
  readonly isMembershipSelectionCompleted: boolean;
  readonly isWelcomePackSelectCompleted: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class RegistrationProgressService {
  constructor(
    private featureFlagService: FeatureFlagsService,
    private registrationFlowService: RegistrationFlowService
  ) {}

  public getRegistrationProgressData(user: User): RegistrationProgressData {
    const isFullNameRegistrationCompleted =
      this.getFullNameRegistrationState(user);
    const isPetChipNumberRegistrationCompleted =
      this.getPetChipNumberRegistrationState(user);
    const isMembershipSelectionCompleted = this.getMembershipSelectionState();
    const isWelcomePackSelectCompleted = this.featureFlagService.isEnabled(
      'isPetPlanFreeInsuranceStep4Available'
    )
      ? this.getWelcomeGiftCompletedState()
      : true;
    return {
      isFullNameRegistrationCompleted: isFullNameRegistrationCompleted,
      isPetChipNumberRegistrationCompleted:
        isPetChipNumberRegistrationCompleted,
      isMembershipSelectionCompleted: isMembershipSelectionCompleted,
      isWelcomePackSelectCompleted: isWelcomePackSelectCompleted,
    };
  }

  private getFullNameRegistrationState(user: User): boolean {
    return !!user.name?.firstName;
  }

  private getPetChipNumberRegistrationState(user: User): boolean {
    if (user.pets.length === 0) {
      return user.hasOpenTokRequests;
    }

    // The user has pets and all of the pets details are filled --> the step is complete
    if (
      user.pets.length > 0 &&
      user.pets.filter(
        (p) => p.name === null || p.name === '[to_be_completed_by_keeper]'
      ).length === 0
    ) {
      return true;
    }

    // If there are incomplete pets, but the membership selection and welcome gift steps are complete,
    // then the registration is complete, but the user added a new pet later
    const isMemberShipSelectionComplete = this.getMembershipSelectionState();
    const isWelcomeGiftStepComplete = this.getWelcomeGiftCompletedState();
    if (isMemberShipSelectionComplete && isWelcomeGiftStepComplete) return true;

    return false;
  }

  // The membership and welcome gift steps count as done once the user has moved past them, or when
  // their flow doesn't have them. Both need the steps loaded.
  private getMembershipSelectionState(): boolean {
    return this.registrationFlowService.isStepCompleted(RegistrationFlowStep.MembershipOptions);
  }

  public getWelcomeGiftCompletedState(): boolean {
    return this.registrationFlowService.isStepCompleted(RegistrationFlowStep.WelcomeGift);
  }
}
